import { useState } from "react";
import { toast } from "sonner";
import { MapPin, LocateFixed, X } from "lucide-react";

export const RADIUS_OPTIONS = [2, 5, 10, 25, 50];

export function useGeoLocate() {
  const [locating, setLocating] = useState(false);
  const locate = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) { toast.error("Your browser does not support location."); resolve(null); return; }
      setLocating(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => { setLocating(false); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
        () => { setLocating(false); toast.error("We couldn't get your location. Please allow location access."); resolve(null); },
        { timeout: 10000 }
      );
    });
  return { locate, locating };
}

const selectCls = "px-4 min-h-[48px] rounded-xl border border-[#E2D9CE] bg-white focus:border-[#D96B43] outline-none font-medium";

export default function LocationFilter({ geo, setGeo, area, setArea, savedCoords }) {
  const { locate, locating } = useGeoLocate();

  const useMyLocation = async () => {
    const c = await locate();
    if (c) setGeo({ ...c, radius_km: geo.radius_km || 10 });
  };
  const useSaved = () => setGeo({ lat: savedCoords.lat, lng: savedCoords.lng, radius_km: geo.radius_km || 10 });

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative">
        <MapPin className="w-5 h-5 text-[#2E7D6B] absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="filter-area" value={area} onChange={(e) => setArea(e.target.value)} placeholder="City or area"
          className={`${selectCls} pl-10 w-48`} />
      </div>
      {geo.lat != null ? (
        <>
          <select data-testid="filter-radius" value={geo.radius_km} onChange={(e) => setGeo({ ...geo, radius_km: Number(e.target.value) })} className={selectCls}>
            {RADIUS_OPTIONS.map((r) => <option key={r} value={r}>Within {r} km</option>)}
          </select>
          <button type="button" data-testid="clear-location-btn" onClick={() => setGeo({})}
            className="flex items-center gap-1 text-slate-600 hover:text-red-600 font-medium min-h-[48px] px-2">
            <X className="w-4 h-4" /> Clear nearby
          </button>
        </>
      ) : (
        <>
          <button type="button" data-testid="use-my-location-btn" onClick={useMyLocation} disabled={locating}
            className="flex items-center gap-2 bg-[#E8F4F1] hover:bg-[#d6ebe5] text-[#2E7D6B] font-semibold px-4 min-h-[48px] rounded-xl transition-colors disabled:opacity-60">
            <LocateFixed className="w-5 h-5" /> {locating ? "Locating…" : "Near me"}
          </button>
          {savedCoords?.lat != null && (
            <button type="button" data-testid="use-saved-location-btn" onClick={useSaved}
              className="text-[#2E7D6B] font-medium min-h-[48px] px-2 underline-offset-2 hover:underline">
              Use my saved home location
            </button>
          )}
        </>
      )}
    </div>
  );
}

export function DistanceTag({ km }) {
  if (km == null) return null;
  return (
    <span data-testid="distance-tag" className="inline-flex items-center gap-1 text-sm font-semibold text-[#2E7D6B]">
      <MapPin className="w-4 h-4" /> {km < 1 ? "under 1 km away" : `${km} km away`}
    </span>
  );
}
